import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Header, Footer, Loading } from "../components/Shell";
import { api, getUser } from "../lib/api";
import { useSeo } from "../lib/seo";

// Sprint 3: Onboarding wizard — 3 steps: Tujuan → Pengalaman → Rekomendasi
// Shown to first-time users after register.
const GOALS = [
  { key: "pemula", label: "Saya baru mulai", desc: "Belum pernah jualan / belum tahu digital marketing.", emoji: "🌱", rec: "fundamental-digital-marketing" },
  { key: "shopee_seller", label: "Saya sudah jualan di Shopee", desc: "Ingin optimasi iklan Shopee & scaling GMV Max.", emoji: "🛒", rec: "iklan-shopee" },
  { key: "tiktok", label: "TikTok Ads / TikTok Shop", desc: "Belajar iklan TikTok — marketplace & social.", emoji: "🎵", rec: "tiktok-gmv-max-ads-seller" },
  { key: "meta", label: "Meta Ads (FB & IG)", desc: "Belajar iklan Facebook & Instagram.", emoji: "📘", rec: "meta-ads-toko-online-sendiri" },
  { key: "scale_up", label: "Sudah profit, mau scale", desc: "Butuh advanced (CPAS, Catalog, Retargeting).", emoji: "📈", rec: "meta-ads-cpas-shopee" },
  { key: "privat", label: "Butuh dibimbing 1-on-1", desc: "Zoom mentoring langsung dengan Mas Tofa.", emoji: "🎯", rec: "" },
];

const EXPS = [
  { key: "pemula", label: "Pemula", desc: "Belum pernah pasang iklan." },
  { key: "pernah_belajar", label: "Pernah belajar", desc: "Sudah baca/nonton tutorial tapi belum praktik." },
  { key: "sudah_iklan", label: "Sudah menjalankan iklan", desc: "Sudah pasang iklan tapi hasilnya belum konsisten." },
  { key: "advanced", label: "Sudah advanced", desc: "Cari materi scaling & profit optimization." },
];

const StepDot = ({ n, current }) => (
  <div style={{
    width: 32, height: 32, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    background: current >= n ? "#2563EB" : "#e2e8f0",
    color: current >= n ? "#fff" : "#94a3b8",
    fontWeight: 700, fontSize: 14, transition: "all 0.2s",
  }}>{current > n ? "✓" : n}</div>
);

export function Onboarding() {
  const user = getUser();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [goal, setGoal] = useState("");
  const [experience, setExperience] = useState("");
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null);

  useSeo({ title: "Setup Akun Anda — Onboarding", path: "/onboarding" });

  useEffect(() => {
    if (!user?.id) {
      nav("/login?next=/onboarding");
      return;
    }
    // If already onboarded, skip to dashboard
    api("/me/onboarding")
      .then((r) => {
        if (r?.onboarding) {
          setExisting(r.onboarding);
        }
      })
      .catch(() => {});
  }, [user?.id, nav]);

  const goalObj = GOALS.find((g) => g.key === goal);
  const recSlug = goal === "privat" ? "" : goalObj?.rec || "";

  const save = async () => {
    setSaving(true);
    try {
      await api("/me/onboarding", {
        method: "POST",
        data: { goal, experience, recommended_path_slug: recSlug },
      });
      // Redirect: privat → /privat, else → course detail
      if (goal === "privat") nav("/privat");
      else if (recSlug) nav(`/courses/${recSlug}`);
      else nav("/dashboard");
    } catch (e) {
      alert("Gagal menyimpan preferensi. Silakan coba lagi.");
      setSaving(false);
    }
  };

  if (!user?.id) return <Loading />;

  return (
    <div>
      <Header />
      <main style={{ minHeight: "calc(100vh - 200px)", background: "#f8fafc", padding: "40px 20px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          {existing ? (
            <div style={{
              background: "#fff", padding: 40, borderRadius: 12,
              textAlign: "center", border: "1px solid #e2e8f0",
            }} data-testid="onboarding-already-done">
              <CheckCircle2 size={48} style={{ color: "#10b981", margin: "0 auto 16px" }} />
              <h2>Anda sudah onboarding sebelumnya</h2>
              <p style={{ color: "#64748b" }}>
                Tujuan: <b>{GOALS.find((g) => g.key === existing.goal)?.label || existing.goal}</b><br />
                Pengalaman: <b>{EXPS.find((e) => e.key === existing.experience)?.label || existing.experience}</b>
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 24 }}>
                <button onClick={() => setExisting(null)} className="button-ghost" data-testid="onboarding-redo">
                  Ulangi Onboarding
                </button>
                <button onClick={() => nav("/dashboard")} className="button" data-testid="onboarding-go-dashboard">
                  Ke Dashboard <ArrowRight size={16} />
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Progress indicator */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: 12, marginBottom: 32,
              }}>
                <StepDot n={1} current={step} /><div style={{ width: 40, height: 2, background: step >= 2 ? "#2563EB" : "#e2e8f0" }} />
                <StepDot n={2} current={step} /><div style={{ width: 40, height: 2, background: step >= 3 ? "#2563EB" : "#e2e8f0" }} />
                <StepDot n={3} current={step} />
              </div>

              <div style={{
                background: "#fff", borderRadius: 16, padding: "40px 32px",
                border: "1px solid #e2e8f0",
              }}>
                {step === 1 && (
                  <div data-testid="onboarding-step-1">
                    <span className="section-kicker">STEP 1 · Kenali Tujuan</span>
                    <h2 style={{ marginTop: 8, marginBottom: 8 }}>Apa tujuan utama Anda?</h2>
                    <p style={{ color: "#64748b", marginBottom: 24 }}>
                      Pilih satu yang paling sesuai dengan kondisi Anda sekarang.
                    </p>
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
                      gap: 12,
                    }}>
                      {GOALS.map((g) => (
                        <button
                          key={g.key}
                          onClick={() => setGoal(g.key)}
                          data-testid={`goal-${g.key}`}
                          style={{
                            padding: 20, borderRadius: 12,
                            background: goal === g.key ? "#eff6ff" : "#fff",
                            border: goal === g.key ? "2px solid #2563EB" : "1px solid #e2e8f0",
                            textAlign: "left", cursor: "pointer",
                            transition: "all 0.15s",
                          }}
                        >
                          <div style={{ fontSize: 28, marginBottom: 8 }}>{g.emoji}</div>
                          <b style={{ display: "block", marginBottom: 4 }}>{g.label}</b>
                          <small style={{ color: "#64748b", lineHeight: 1.4 }}>{g.desc}</small>
                        </button>
                      ))}
                    </div>
                    <div style={{ marginTop: 32, display: "flex", justifyContent: "flex-end" }}>
                      <button
                        className="button"
                        onClick={() => setStep(2)}
                        disabled={!goal}
                        data-testid="step-1-next"
                        style={{ opacity: goal ? 1 : 0.4 }}
                      >
                        Lanjut <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="onboarding-step-2">
                    <span className="section-kicker">STEP 2 · Pengalaman</span>
                    <h2 style={{ marginTop: 8, marginBottom: 8 }}>Seberapa dalam pengalaman Anda?</h2>
                    <p style={{ color: "#64748b", marginBottom: 24 }}>
                      Kami sesuaikan rekomendasi materi supaya cocok level Anda.
                    </p>
                    <div style={{ display: "grid", gap: 12 }}>
                      {EXPS.map((e) => (
                        <button
                          key={e.key}
                          onClick={() => setExperience(e.key)}
                          data-testid={`exp-${e.key}`}
                          style={{
                            padding: "16px 20px", borderRadius: 12,
                            background: experience === e.key ? "#eff6ff" : "#fff",
                            border: experience === e.key ? "2px solid #2563EB" : "1px solid #e2e8f0",
                            textAlign: "left", cursor: "pointer",
                          }}
                        >
                          <b style={{ display: "block", marginBottom: 4 }}>{e.label}</b>
                          <small style={{ color: "#64748b" }}>{e.desc}</small>
                        </button>
                      ))}
                    </div>
                    <div style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }}>
                      <button className="button-ghost" onClick={() => setStep(1)} data-testid="step-2-back">
                        <ArrowLeft size={16} /> Kembali
                      </button>
                      <button
                        className="button"
                        onClick={() => setStep(3)}
                        disabled={!experience}
                        data-testid="step-2-next"
                        style={{ opacity: experience ? 1 : 0.4 }}
                      >
                        Lanjut <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div data-testid="onboarding-step-3">
                    <span className="section-kicker">STEP 3 · Rekomendasi</span>
                    <h2 style={{ marginTop: 8, marginBottom: 8 }}>
                      Jalur belajar yang kami rekomendasikan:
                    </h2>
                    <div style={{
                      background: "linear-gradient(135deg,#eff6ff,#dbeafe)",
                      border: "1px solid #93c5fd", borderRadius: 12,
                      padding: 24, margin: "20px 0",
                    }} data-testid="onboarding-recommendation">
                      <div style={{ fontSize: 40, marginBottom: 12 }}>{goalObj?.emoji}</div>
                      <b style={{ fontSize: "1.2rem", display: "block", marginBottom: 8 }}>
                        {goal === "privat"
                          ? "Privat 1-on-1 dengan Mas Tofa"
                          : goalObj?.label || "Path recommended"}
                      </b>
                      <p style={{ color: "#475569", lineHeight: 1.6 }}>
                        {goal === "privat"
                          ? "Konsultasi & mentoring privat via Zoom. Fokus penuh pada bisnis Anda."
                          : `Kami merekomendasikan mulai dari kursus di atas. Anda akan diarahkan langsung ke materinya setelah klik "Mulai Belajar".`}
                      </p>
                    </div>
                    <p style={{ color: "#64748b", fontSize: "0.85rem" }}>
                      Anda tetap bisa ganti jalur belajar kapan saja lewat menu <b>Belajar</b>.
                    </p>
                    <div style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }}>
                      <button className="button-ghost" onClick={() => setStep(2)} data-testid="step-3-back">
                        <ArrowLeft size={16} /> Kembali
                      </button>
                      <button
                        className="button"
                        onClick={save}
                        disabled={saving}
                        data-testid="onboarding-finish"
                        style={{ background: "#10b981" }}
                      >
                        {saving ? "Menyimpan..." : "Mulai Belajar"} <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
